'use client'

import Image from 'next/image'
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { ABHA_HERO_EXISTING_ABHA_LOCK_ICON } from '@components/NewProduct/constants'
import { getAbhaProfileInitials } from '@helpers/abhaWidgetHelper'
import { getClassNames } from '@helpers/productWidgetHelper'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import styles from './abhaHeroCardCarousel.module.scss'

export type AbhaHeroCardCarouselItem = {
  id: string
  profileImageUrl?: string
  name?: string
  isCardFetched?: boolean
}

type AbhaHeroCardCarouselProps = {
  items: AbhaHeroCardCarouselItem[]
  renderCard: (index: number) => ReactNode
  onActiveIndexChange?: (index: number, source: 'avatar' | 'scroll') => void
  className?: string
  ariaLabel?: string
}

export const AbhaHeroCardCarousel = ({
  items,
  renderCard,
  onActiveIndexChange,
  className,
  ariaLabel = 'Select ABHA card',
}: AbhaHeroCardCarouselProps) => {
  const carouselId = useId()
  const carouselRef = useRef<HTMLDivElement>(null)
  const isProgrammaticScrollRef = useRef(false)
  const activeIndexRef = useRef(0)
  const settleTimerRef = useRef<number | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const updateActiveIndex = useCallback(
    (index: number, source: 'avatar' | 'scroll') => {
      activeIndexRef.current = index
      setActiveIndex(index)
      onActiveIndexChange?.(index, source)
    },
    [onActiveIndexChange]
  )

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth', source: 'avatar' | 'scroll' = 'scroll') => {
      const carousel = carouselRef.current
      if (!carousel || items.length === 0) return

      const clampedIndex = Math.max(0, Math.min(items.length - 1, index))
      const target = carousel.querySelector<HTMLElement>(`[data-card-index="${clampedIndex}"]`)
      if (!target) return

      isProgrammaticScrollRef.current = true
      updateActiveIndex(clampedIndex, source)
      target.scrollIntoView({ behavior, inline: 'start', block: 'nearest' })

      window.setTimeout(
        () => {
          isProgrammaticScrollRef.current = false
        },
        behavior === 'smooth' ? 400 : 0
      )
    },
    [items.length, updateActiveIndex]
  )

  const settleCarousel = useCallback(() => {
    const carousel = carouselRef.current
    if (!carousel || isProgrammaticScrollRef.current) return

    const cards = Array.from(carousel.querySelectorAll<HTMLElement>('[data-card-index]'))
    if (cards.length === 0) return

    const carouselLeft = carousel.scrollLeft
    let nearestIndex = 0
    let nearestDistance = Number.POSITIVE_INFINITY

    cards.forEach(card => {
      const distance = Math.abs(card.offsetLeft - carouselLeft)

      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = Number(card.dataset.cardIndex)
      }
    })

    const previousIndex = activeIndexRef.current
    const clampedIndex = Math.max(previousIndex - 1, Math.min(previousIndex + 1, nearestIndex))

    if (clampedIndex !== nearestIndex || clampedIndex !== previousIndex) {
      scrollToIndex(clampedIndex)
      return
    }

    updateActiveIndex(clampedIndex, 'scroll')
  }, [scrollToIndex, updateActiveIndex])

  useEffect(() => {
    const lastIndex = Math.max(items.length - 1, 0)

    if (activeIndexRef.current > lastIndex) {
      updateActiveIndex(lastIndex, 'scroll')
    }
  }, [items.length, updateActiveIndex])

  useEffect(() => {
    const carousel = carouselRef.current
    if (!carousel) return undefined

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return

      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current)
      }

      settleTimerRef.current = window.setTimeout(settleCarousel, 80)
    }

    carousel.addEventListener('scroll', handleScroll, { passive: true })
    carousel.addEventListener('scrollend', settleCarousel)

    return () => {
      carousel.removeEventListener('scroll', handleScroll)
      carousel.removeEventListener('scrollend', settleCarousel)

      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current)
      }
    }
  }, [items.length, settleCarousel])

  if (items.length === 0) return null

  return (
    <div className={getClassNames([styles.root, className ?? ''], {})}>
      {items.length > 1 && (
        <div className={styles.avatarRow} role="tablist" aria-label={ariaLabel}>
          {items.map((item, index) => {
            const isActive = index === activeIndex
            const cardId = `${carouselId}-card-${index}`

            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={cardId}
                className={styles.avatarButton}
                onClick={() => {
                  if (!isActive) scrollToIndex(index, 'smooth', 'avatar')
                }}>
                <span
                  className={getClassNames([styles.avatarRing], {
                    [styles.avatarRingActive]: isActive,
                    [styles.avatarRingInactive]: !isActive,
                    [styles.avatarRingFallback]: !item.profileImageUrl,
                  })}>
                  <PropertyControlledComponent
                    controllerProperty={!!item.profileImageUrl}
                    fallback={
                      <span className={styles.avatarFallback} aria-hidden>
                        {getAbhaProfileInitials(item.name)}
                      </span>
                    }>
                    <Image
                      src={item.profileImageUrl || ''}
                      alt=""
                      width={isActive ? 48 : 40}
                      height={isActive ? 48 : 40}
                      unoptimized={item.profileImageUrl?.startsWith('data:')}
                      className={styles.avatarImage}
                      aria-hidden
                    />
                  </PropertyControlledComponent>
                </span>
                <PropertyControlledComponent controllerProperty={!item.isCardFetched} fallback={null}>
                  <span
                    className={getClassNames([styles.avatarLock], {
                      [styles.avatarLockActive]: isActive,
                    })}
                    aria-hidden>
                    <Image
                      src={ABHA_HERO_EXISTING_ABHA_LOCK_ICON}
                      alt=""
                      width={24}
                      height={24}
                      className={styles.avatarLockIcon}
                    />
                  </span>
                </PropertyControlledComponent>
              </button>
            )
          })}
        </div>
      )}

      <div ref={carouselRef} className={styles.carousel} aria-live="polite">
        {items.map((item, index) => (
          <article
            key={item.id}
            id={`${carouselId}-card-${index}`}
            data-card-index={index}
            className={styles.carouselItem}
            aria-hidden={index !== activeIndex}>
            {renderCard(index)}
          </article>
        ))}
      </div>
    </div>
  )
}
